import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../../configureStore";
import { authSession, logOut } from "./auth.service";

// Define the state type
interface AuthState {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  session: any;
  userMenuActive: boolean;
  navActive: boolean;
  isLoggedIn: boolean;
  language: string;
  isCheckingSession: boolean;
  hasInitialSessionCheck: boolean;
}

// Define the initial state
const initialState: AuthState = {
  isLoading: false,
  isError: false,
  errorMessage: null,
  session: null,
  userMenuActive: false,
  navActive: false,
  isLoggedIn: false,
  language: "lt",
  isCheckingSession: false,
  hasInitialSessionCheck: false,
};

// Create the slice
const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession(state, action: PayloadAction<{ session: any }>) {
      // Replace 'any' with the actual session type if known
      state.session = action.payload.session;
      state.isLoggedIn = true;
      state.isLoading = false;
      state.isError = false;
      state.errorMessage = null;
      state.isCheckingSession = false;
      state.hasInitialSessionCheck = true;
    },
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
    },
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.session = null;
      state.isError = true;
      state.isLoggedIn = false;
      state.errorMessage = action.payload;
      state.isCheckingSession = false;
      state.hasInitialSessionCheck = true;
    },
    setLogout(state) {
      state.isLoading = false;
      state.session = null;
      state.isLoggedIn = false;
      state.isError = false;
      state.errorMessage = null;
      state.isCheckingSession = false;
      state.hasInitialSessionCheck = true;
    },
    setLanguage(state, action) {
      state.language = action.payload;
    },
    setUserMenuActive(state) {
      state.userMenuActive = !state.userMenuActive;
    },
    setNavActive(state) {
      state.navActive = !state.navActive;
    },
    setCheckingSession(state, action: PayloadAction<boolean>) {
      state.isCheckingSession = action.payload;
    },
  },
  /* eslint-enable */
});

// Extract the action creators object and the reducer
const { actions, reducer } = AuthSlice;

// Extract and export each action creator by name
export const {
  setSession,
  setLoading,
  setError,
  setLogout,
  setUserMenuActive,
  setNavActive,
  setCheckingSession,
} = actions;
// Export the reducer, either as a default or named export
export default reducer;

export const toogleUserMenu = () => async (dispatch: AppDispatch) => {
  dispatch(setUserMenuActive());
};

export const toogleNav = () => async (dispatch: AppDispatch) => {
  dispatch(setNavActive());
};

// Global flag to prevent multiple simultaneous session checks
let isSessionCheckInProgress = false;

export const checkSession = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  
  // Prevent multiple simultaneous session checks
  if (isSessionCheckInProgress || state.authReducer.isCheckingSession || state.authReducer.isLoading) {
    return false;
  }
  
  // Set the global flag to prevent concurrent calls
  isSessionCheckInProgress = true;
  dispatch(setCheckingSession(true));
  
  try {
    const response = await authSession();
    
    // Handle different possible response formats
    if (response && response.status === 'success' && response.user) {
      // Format matches login response
      const sessionData = {
        ...response.user,
        email: `${response.user.name_user} ${response.user.surname_user}`,
        session_id: response.session_user,
        position: response.user.title_position,
        department: response.user.title_department
      };
      dispatch(setSession({ session: sessionData }));
      return true;
    } else if (response && response.user && !response.status) {
      // Handle case where response might just be user data
      const sessionData = {
        ...response.user,
        email: `${response.user.name_user} ${response.user.surname_user}`,
        session_id: response.session_user,
        position: response.user.title_position,
        department: response.user.title_department
      };
      dispatch(setSession({ session: sessionData }));
      return true;
    } else if (response && response.name_user) {
      // Handle case where response is directly the user object
      const sessionData = {
        ...response,
        email: `${response.name_user} ${response.surname_user}`,
        position: response.title_position,
        department: response.title_department
      };
      dispatch(setSession({ session: sessionData }));
      return true;
    } else {
      // Clear session state properly for logged out users
      dispatch(setLogout()); // This will clear session and set isLoggedIn = false
      dispatch(setCheckingSession(false));
      return false;
    }
  } catch (e: unknown) {
    // Only set error for actual network/server errors
    if (e && typeof e === 'object' && 'response' in e) {
      const axiosError = e as { response: { status: number } };
      if (axiosError.response && axiosError.response.status !== 401 && axiosError.response.status !== 403) {
        dispatch(setError("Session check failed"));
      } else {
        dispatch(setLogout()); // Clear session state
        dispatch(setCheckingSession(false));
      }
    } else {
      dispatch(setLogout()); // Clear session state
      dispatch(setCheckingSession(false));
    }
    return false;
  } finally {
    // Always reset the global flag
    isSessionCheckInProgress = false;
  }
};

export const logout = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading());
  try {
    await logOut();
    dispatch(setLogout());
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : 'Logout failed';
    dispatch(setError(errorMessage));
  }
};
